import { useRef } from 'react';
import { motion, useMotionValue, useSpring } from 'motion/react';

interface MagneticButtonProps {
  children: React.ReactNode;
  onClick?: () => void;
  className?: string;
  style?: React.CSSProperties;
  strength?: number;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary';
  'aria-label'?: string;
}

export function MagneticButton({
  children,
  onClick,
  className = '',
  style,
  strength = 0.18,
  type = 'button',
  variant = 'primary',
  ...rest
}: MagneticButtonProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const glowX = useMotionValue(50);
  const glowY = useMotionValue(50);
  const springX = useSpring(x, { stiffness: 260, damping: 20, mass: 0.45 });
  const springY = useSpring(y, { stiffness: 260, damping: 20, mass: 0.45 });

  const handleMouse = (event: React.MouseEvent<HTMLButtonElement>) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const localX = event.clientX - rect.left;
    const localY = event.clientY - rect.top;
    x.set((localX - rect.width / 2) * strength);
    y.set((localY - rect.height / 2) * strength);
    glowX.set((localX / rect.width) * 100);
    glowY.set((localY / rect.height) * 100);
  };

  const reset = () => {
    x.set(0);
    y.set(0);
    glowX.set(50);
    glowY.set(50);
  };

  return (
    <motion.button
      ref={ref}
      type={type}
      onMouseMove={handleMouse}
      onMouseLeave={reset}
      onClick={onClick}
      style={{ x: springX, y: springY, ...style }}
      whileTap={{ scale: 0.98 }}
      className={`keevo-magnetic keevo-magnetic--${variant} ${className}`}
      {...rest}
    >
      <motion.span
        className="keevo-magnetic__glow"
        style={{ left: glowX, top: glowY }}
        aria-hidden="true"
      />
      <span className="keevo-magnetic__content">{children}</span>
    </motion.button>
  );
}
